/**
 * Header Component
 * Manages header interactions: search, cart, mobile menu
 * 
 * Note: Mega menu functionality is handled by mega-menu.js
 * @requires ThemeUtils (utils.js) - optional, uses fallbacks if not available
 */

class Header {
  constructor() {
    this.mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
    this.init();
  }

  init() {
    this.bindSearchButtons();
    this.bindCartButtons();
    this.bindLocalizationButtons();
    this.bindMobileMenu();
    this.bindMobileMenuLinks();
  }

  /**
   * Dispatch custom event helper
   */
  dispatchEvent(eventName, detail = {}) {
    if (window.ThemeUtils?.dispatchEvent) {
      window.ThemeUtils.dispatchEvent(eventName, detail);
    } else {
      document.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true }));
    }
  }

  _setThemeColor(color) {
    const existing = document.querySelector('meta[name="theme-color"]');
    if (existing) existing.remove();
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = color;
    document.head.appendChild(meta);
  }

  /**
   * Bind search button clicks
   */
  bindSearchButtons() {
    const searchButtons = document.querySelectorAll('.js-toggle-search');
    
    searchButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeMobileMenuIfOpen();
        this.openDrawer('search', 'right');
      });
    });
  }

  /**
   * Bind cart button clicks
   */
  bindCartButtons() {
    const cartButtons = document.querySelectorAll('.js-toggle-cart');
    
    cartButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.openDrawer('cart', 'right');
      });
    });
  }

  /**
   * Bind localization button clicks (language/currency selector)
   */
  bindLocalizationButtons() {
    const localizationButtons = document.querySelectorAll('.js-toggle-localization');
    
    localizationButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeMobileMenuIfOpen();
        this.openDrawer('localization', 'right');
      });
    });
  }

  /**
   * Bind mobile menu toggle
   */
  bindMobileMenu() {
    const burgerButtons = document.querySelectorAll('.js-toggle-mobile-menu');
    
    burgerButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleMobileMenu();
      });
    });
  }

  /**
   * Bind click events on mobile menu links
   */
  bindMobileMenuLinks() {
    if (!this.mobileMenuOverlay) return;

    this.mobileMenuOverlay.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      
      // Close menu when clicking a regular link (not submenu)
      if (link && !link.closest('.has-submenu')) {
        setTimeout(() => this.closeMobileMenu(), 150);
      }
    });
  }

  /**
   * Open a drawer via custom event
   */
  openDrawer(drawerId, position = 'right') {
    this.dispatchEvent('openDrawer', { drawerId, position });
  }

  /**
   * Toggle mobile menu state
   */
  toggleMobileMenu() {
    if (!this.mobileMenuOverlay) return;

    const isOpen = this.mobileMenuOverlay.getAttribute('aria-hidden') === 'false';
    
    if (isOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  /**
   * Open mobile menu
   */
  openMobileMenu() {
    if (!this.mobileMenuOverlay) return;

    // Update Safari status/address bar color before any DOM changes
    const headerBg = getComputedStyle(document.documentElement).getPropertyValue('--color-header-background').trim();
    if (headerBg) this._setThemeColor(headerBg);

    document.body.classList.add('mobile-menu-open');
    document.body.style.overflow = 'hidden';
    this.mobileMenuOverlay.setAttribute('aria-hidden', 'false');
    
    this.dispatchEvent('mobileMenuOpen');
  }

  /**
   * Close mobile menu
   */
  closeMobileMenu() {
    if (!this.mobileMenuOverlay) return;

    this.mobileMenuOverlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.body.classList.remove('mobile-menu-open');

    // Restore Safari status/address bar color
    const headerBg = getComputedStyle(document.documentElement).getPropertyValue('--color-header-background').trim();
    if (headerBg) this._setThemeColor(headerBg);
    
    this.dispatchEvent('mobileMenuClose');
  }

  /**
   * Close mobile menu if it's currently open
   */
  closeMobileMenuIfOpen() {
    if (!this.mobileMenuOverlay) return;

    const isOpen = this.mobileMenuOverlay.getAttribute('aria-hidden') === 'false';
    if (isOpen) {
      this.closeMobileMenu();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.header = new Header();
});